import { useCallback, useEffect, useMemo, useState } from 'react';
import { Chip, TableCell, TableRow, Checkbox, Box, TableHead, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { IPagingData, LeadInfoDto, LeadPermissions } from '@sr/dto';
import { GridLayout } from 'shared/ui/grid-layout';
import { formatDateTime } from 'utils/date-format-helpers';
import { LinkToLead } from 'shared/ui/links/link-to-lead';
import { useLeads } from '../leads.hook';
import { LeadsFilter } from 'features/lead/leads-filter/leads-filter';
import { useSearchParams } from 'react-router-dom';
import { LinkToCompany } from 'shared/ui/links/link-to-company';
import ManagerSpan from 'shared/ui/manager-span';
import { LeadBulkUpdate } from 'features/lead/lead-bulk-update/lead-bulk-update';
import { statusCounter } from 'utils/status-counter-helper';
import { LEAD_STATUS_IN_PROGRESS, LEAD_STATUS_NEW, LEAD_STATUS_NOT_PROCESSED, LEAD_STATUS_WAITING_FOR_DECISION, LEADS_STATUSES, LeadStatus } from '@sr/dto/dist/src/enums/leads';
import { ChipFilter } from 'features/lead/leads-filter/chip-meta-url-param-filter';
import { formatLeadSourceType } from '../lead-source-format.helper';
import { LeadSourceLabel } from '../lead-source-label';
import { useUserAuth } from 'features/auth';

const columns = ['', 'Лид', 'Тема', 'Статус', 'Фирма', 'Менеджер', 'Тип источника', 'Источник', 'Дата создания'];
const readOnlyColumns = columns.slice(1);

type Props = {
  companyId?: number;
  paging: IPagingData;
  onChangePaging?: (paging: IPagingData) => void;
  readOnly?: boolean;
  customSearchParams?: URLSearchParams;
  withCounts?: boolean;
  isDesktop?: boolean;
};

export const LeadsList = ({ paging, onChangePaging, companyId, customSearchParams, readOnly = false, withCounts = false, isDesktop }: Props) => {
  const [searchParams] = useSearchParams();
  const { user } = useUserAuth();

  const mergedParams = new URLSearchParams(customSearchParams || searchParams);
  if (companyId) {
    mergedParams.set('companyId', companyId.toString());
  }
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [excludedIds, setExcludedIds] = useState<number[]>([]);
  const [allFilteredSelected, setAllFilteredSelected] = useState(false);
  const [selectionMenuAnchor, setSelectionMenuAnchor] = useState<HTMLElement | null>(null);
  const { collection, isLoading, isFetching, error, refetch } = useLeads(paging, mergedParams);

  const mergedParamsString = mergedParams.toString();
  const filterParams = useMemo(() => {
    const params = new URLSearchParams(mergedParamsString);
    params.delete('page');
    params.delete('itemsPerPage');
    if (user) {
      if (user.permissions.includes(LeadPermissions.ACCESS_STATUS_NEW)) params.set('showNew', 'true');
      if (user.permissions.includes(LeadPermissions.ACCESS_STATUS_UNKNOWN)) params.set('showUnknown', 'true');
      if (user.permissions.includes(LeadPermissions.ADMIN)) params.set('showAll', 'true');
    }
    return params;
  }, [mergedParamsString, user]);

  const filterKey = filterParams.toString();
  const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const excludedIdsSet = useMemo(() => new Set(excludedIds), [excludedIds]);
  const pageIds = collection.items.map((lead) => lead.id);
  const filteredTotal = collection.totalItemsCount ?? 0;
  const selectedCount = allFilteredSelected ? Math.max(filteredTotal - excludedIds.length, 0) : selectedIds.length;
  const selectedOnPageCount = pageIds.filter((id) => allFilteredSelected ? !excludedIds.includes(id) : selectedIds.includes(id)).length;

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setExcludedIds([]);
    setAllFilteredSelected(false);
  }, []);

  useEffect(() => {
    clearSelection();
  }, [clearSelection, filterKey]);

  const allStatusIds = Object.keys(LEADS_STATUSES).map((key) => Number(key));
  const statusIds = isDesktop
    ? [LEAD_STATUS_NEW, LEAD_STATUS_NOT_PROCESSED, LEAD_STATUS_IN_PROGRESS, LEAD_STATUS_WAITING_FOR_DECISION]
    : [LEAD_STATUS_NEW, ...allStatusIds.filter((statusId) => statusId !== LEAD_STATUS_NEW)];
  const statuses = statusCounter(statusIds, 'statusId', collection.meta);

  const handleCurrentPageSelection = useCallback((checked: boolean) => {
    if (!pageIds.length) return;

    if (allFilteredSelected) {
      setExcludedIds((prev) => checked
        ? prev.filter((id) => !pageIds.includes(id))
        : Array.from(new Set([...prev, ...pageIds])));
      return;
    }

    setSelectedIds((prev) => checked
      ? Array.from(new Set([...prev, ...pageIds]))
      : prev.filter((id) => !pageIds.includes(id)));
  }, [allFilteredSelected, pageIds]);

  const selectCurrentPage = () => {
    setSelectedIds(pageIds);
    setExcludedIds([]);
    setAllFilteredSelected(false);
    setSelectionMenuAnchor(null);
  };

  const selectAllPages = () => {
    setSelectedIds([]);
    setExcludedIds([]);
    setAllFilteredSelected(true);
    setSelectionMenuAnchor(null);
  };

  const clearSelectionFromMenu = () => {
    clearSelection();
    setSelectionMenuAnchor(null);
  };

  const handleLeadToggle = useCallback((leadId: number) => {
    if (allFilteredSelected) {
      setExcludedIds((prev) => (prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]));
      return;
    }

    setSelectedIds((prev) => (prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]));
  }, [allFilteredSelected]);

  const handleBulkUpdated = useCallback(async () => {
    clearSelection();
    await refetch();
  }, [clearSelection, refetch]);

  const renderCustomHeader = () => {
    return (
      <TableHead>
        <TableRow sx={{ height: 56 }}>
          <TableCell padding="checkbox">
            <Box display="flex" alignItems="center">
              <Tooltip title="Выбрать лиды на текущей странице">
                <span>
                  <Checkbox
                    indeterminate={selectedOnPageCount > 0 && selectedOnPageCount < pageIds.length}
                    checked={pageIds.length > 0 && selectedOnPageCount === pageIds.length}
                    onChange={(_event, checked) => handleCurrentPageSelection(checked)}
                    disabled={isLoading || !pageIds.length}
                    sx={{ padding: 0 }}
                    inputProps={{ 'aria-label': 'Выбрать лиды на текущей странице' }}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Выбрать область">
                <span>
                  <IconButton
                    size="small"
                    onClick={(event) => setSelectionMenuAnchor(event.currentTarget)}
                    disabled={isLoading || !filteredTotal}
                    aria-label="Выбрать область"
                    aria-haspopup="menu"
                  >
                    <ArrowDropDownIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Menu anchorEl={selectionMenuAnchor} open={Boolean(selectionMenuAnchor)} onClose={() => setSelectionMenuAnchor(null)}>
                <MenuItem onClick={selectCurrentPage}>Выбрать текущую страницу ({pageIds.length})</MenuItem>
                <MenuItem onClick={selectAllPages}>Выбрать все страницы ({filteredTotal})</MenuItem>
                <MenuItem onClick={clearSelectionFromMenu}>Снять выбор</MenuItem>
              </Menu>
            </Box>
          </TableCell>
          {columns.slice(1).map((column) => (
            <TableCell key={column}>{column}</TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  return (
    <>
      <>
        {!readOnly && <LeadsFilter />}
        {selectedCount > 0 && !readOnly && (
          <LeadBulkUpdate selectedIds={selectedIds} excludedIds={excludedIds} all={allFilteredSelected} filter={filterKey} selectedCount={selectedCount} onUpdated={handleBulkUpdated} />
        )}
        {withCounts && !isLoading && (
          <Box display="flex" flexWrap="wrap" gap={2} my={2} component="div" alignItems="baseline" sx={{ opacity: isFetching ? 0.7 : 1 }}>
            <>
              <Chip label={`Всего лидов: ${collection.totalItemsCount}`} color="primary" variant="outlined" />
              {isDesktop && <div>Отображаются данные со статусами</div>}
              <ChipFilter
                items={statuses.map((item) => ({ id: item.statusId, name: LEADS_STATUSES[item.statusId as LeadStatus], count: item.count }))}
                readOnly={readOnly}
                queryParamName="statusId"
              />
            </>
          </Box>
        )}
      </>
      <GridLayout
        columns={readOnly ? readOnlyColumns : columns}
        items={collection.items}
        isLoading={isLoading}
        error={error}
        pagingData={paging}
        onChangePaging={onChangePaging}
        totalItemsCount={collection.totalItemsCount}
        noItemsText="Список лидов пуст"
        itemRender={(i) => <LeadRow readOnly={readOnly} selected={allFilteredSelected ? !excludedIdsSet.has(i.id) : selectedIdsSet.has(i.id)} onToggle={handleLeadToggle} key={`${i.id} ${i.status}`} lead={i} />}
        renderTableHeader={!readOnly ? renderCustomHeader : undefined}
      />
    </>
  );
};

type RowProps = {
  lead: LeadInfoDto;
  selected: boolean;
  onToggle: (leadId: number) => void;
  readOnly: boolean;
};

export const LeadRow = (props: RowProps) => {
  const { lead, selected, onToggle, readOnly } = props;
  return (
    <>
      <TableRow key={lead.id}>
        {!readOnly && (
          <TableCell width={50} component="th" scope="row">
            <Checkbox checked={selected} onChange={() => onToggle(lead.id)} sx={{ padding: 0 }} />
          </TableCell>
        )}

        <TableCell>
          <LinkToLead lead={lead}>{lead.id}</LinkToLead>
        </TableCell>
        <TableCell>{lead.subject || '-'}</TableCell>
        <TableCell>{lead.status ? <Chip label={lead.status.label} /> : '-'}</TableCell>
        <TableCell>
          {lead.companies
            ? lead.companies.map((item) => (
              <div key={`${item.id}`}>
                <LinkToCompany company={item} />
              </div>
            ))
            : '-'}
        </TableCell>
        <TableCell>{lead.manager ? <ManagerSpan person={lead.manager} /> : '-'}</TableCell>

        <TableCell>{formatLeadSourceType(lead.sourceType)}</TableCell>
        <TableCell><LeadSourceLabel sourceType={lead.sourceType} sourceValue={lead.sourceValue} source2Name={lead.source2Name} /></TableCell>
        <TableCell>{formatDateTime(lead.createdAt)}</TableCell>
      </TableRow>
    </>
  );
};
